// Header.tsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, Variants, easeInOut } from 'framer-motion';
import { FaBars, FaTimes, FaUserCircle } from 'react-icons/fa';
import { useLocation, Link } from 'react-router-dom';
import logo from '../assets/logo.png';
import { useTranslation } from 'react-i18next';

// New component for the Sign-in/Login Modal
const AuthModal: React.FC<{ isOpen: boolean; onClose: () => void; onLoginSuccess: (name: string) => void }> = ({ isOpen, onClose, onLoginSuccess }) => {
    const { t } = useTranslation();

    const [isLogin, setIsLogin] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [name, setName] = useState('');
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);

    useEffect(() => {
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setName('');
        setMessage('');
        setIsError(false);
    }, [isLogin, isOpen]);

    if (!isOpen) return null;

    const modalVariants: Variants = {
        hidden: {
            opacity: 0,
            scale: 0.95,
        },
        visible: {
            opacity: 1,
            scale: 1,
            transition: {
                duration: 0.3,
                ease: easeInOut,
            },
        },
        exit: {
            opacity: 0,
            scale: 0.95,
            transition: {
                duration: 0.2,
                ease: easeInOut,
            },
        },
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setMessage('');
        setIsError(false);

        const url = isLogin ? 'http://localhost:8383/api/auth/login' : 'http://localhost:8383/api/auth/signup';
        let body: any = { email, password };

        if (!isLogin) {
            if (password !== confirmPassword) {
                setMessage(t('passwords_do_not_match'));
                setIsError(true);
                return;
            }
            body = { ...body, name };
        }

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
                credentials: 'include' // <-- THIS IS THE KEY FIX
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setMessage(data.message);
                setIsError(false);
                if (isLogin) {
                    onLoginSuccess(data.name);
                    setTimeout(() => {
                        onClose();
                    }, 500);
                } else {
                    setMessage(t('registration_successful_login'));
                    setIsLogin(true);
                }
            } else {
                setMessage(data.message || t('an_error_occurred'));
                setIsError(true);
            }
        } catch (error) {
            console.error('Fetch error:', error);
            setMessage(t('network_error_backend_check'));
            setIsError(true);
        }
    };

    return (
        <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
        >
            <motion.div
                className="bg-white rounded-lg p-8 shadow-xl max-w-md w-full relative"
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
                >
                    <FaTimes size={20} />
                </button>

                <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                    {isLogin ? t('sign_in') : t('sign_up')}
                </h2>

                <div className="flex justify-center mb-6 border-b border-gray-200">
                    <button
                        type="button"
                        className={`py-2 px-4 text-lg font-medium ${isLogin ? 'text-[#1A237E] border-b-2 border-[#AEEA00]' : 'text-gray-500 hover:text-gray-700'}`}
                        onClick={() => { setIsLogin(true); setMessage(''); setIsError(false); }}
                    >
                        {t('login_tab')}
                    </button>
                    <button
                        type="button"
                        className={`py-2 px-4 text-lg font-medium ${!isLogin ? 'text-[#1A237E] border-b-2 border-[#AEEA00]' : 'text-gray-500 hover:text-gray-700'}`}
                        onClick={() => { setIsLogin(false); setMessage(''); setIsError(false); }}
                    >
                        {t('signup_tab')}
                    </button>
                </div>

                {message && (
                    <div className={`mb-4 p-3 rounded text-center ${isError ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                        {message}
                    </div>
                )}

                <form className="space-y-4" onSubmit={handleSubmit}>
                    {!isLogin && (
                        <div>
                            <label htmlFor="auth-name" className="block text-gray-700 text-sm font-bold mb-2">
                                {t('name_label')}
                            </label>
                            <input
                                type="text"
                                id="auth-name"
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-[#AEEA00]"
                                placeholder={t('your_name_placeholder')}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />
                        </div>
                    )}
                    <div>
                        <label htmlFor="auth-email" className="block text-gray-700 text-sm font-bold mb-2">
                            {t('email_label')}
                        </label>
                        <input
                            type="email"
                            id="auth-email"
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-[#AEEA00]"
                            placeholder={t('email_placeholder')}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </div>
                    <div>
                        <label htmlFor="auth-password" className="block text-gray-700 text-sm font-bold mb-2">
                            {t('password_label')}
                        </label>
                        <input
                            type="password"
                            id="auth-password"
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 mb-3 leading-tight focus:outline-none focus:ring-2 focus:ring-[#AEEA00]"
                            placeholder="********"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={6}
                        />
                    </div>
                    {!isLogin && (
                        <div>
                            <label htmlFor="auth-confirm-password" className="block text-gray-700 text-sm font-bold mb-2">
                                {t('confirm_password_label')}
                            </label>
                            <input
                                type="password"
                                id="auth-confirm-password"
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 mb-3 leading-tight focus:outline-none focus:ring-2 focus:ring-[#AEEA00]"
                                placeholder="********"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                            />
                        </div>
                    )}
                    <button
                        type="submit"
                        className="bg-[#AEEA00] text-[#1A237E] font-bold py-2 px-4 rounded-full w-full hover:bg-lime-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-lime-400"
                    >
                        {isLogin ? t('login_button') : t('create_account_button')}
                    </button>
                </form>

                {isLogin && (
                    <p className="text-center text-gray-600 text-sm mt-4">
                        {t('dont_have_account')}{' '}
                        <button
                            type="button"
                            onClick={() => setIsLogin(false)}
                            className="text-[#1A237E] hover:underline"
                        >
                            {t('signup_link')}
                        </button>
                    </p>
                )}
                {!isLogin && (
                    <p className="text-center text-gray-600 text-sm mt-4">
                        {t('already_have_account')}{' '}
                        <button
                            type="button"
                            onClick={() => setIsLogin(true)}
                            className="text-[#1A237E] hover:underline"
                        >
                            {t('login_link')}
                        </button>
                    </p>
                )}
            </motion.div>
        </motion.div>
    );
};


const Header: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [loggedInUserName, setLoggedInUserName] = useState<string | null>(null);
    const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
    const location = useLocation();

    const isHomePage = location.pathname === '/';

    useEffect(() => {
        const storedUserName = localStorage.getItem('userName');
        if (storedUserName) {
            setLoggedInUserName(storedUserName);
        }
    }, []);

    useEffect(() => {
        if (notification) {
            const timer = setTimeout(() => {
                setNotification(null);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [notification]);

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 50) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        if (isHomePage) {
            window.addEventListener('scroll', handleScroll);
            return () => {
                window.removeEventListener('scroll', handleScroll);
            };
        } else {
            setIsScrolled(false);
        }
    }, [isHomePage]);

    const showNotification = (message: string, type: 'success' | 'error') => {
        setNotification({ message, type });
    };

    const handleLoginSuccess = (name: string) => {
        setLoggedInUserName(name);
        localStorage.setItem('userName', name);
        showNotification(t('welcome_user', { name }), 'success');
    };

    const handleLogout = async () => {
        try {
            const response = await fetch('http://localhost:8383/logout', {
                method: 'POST',
                credentials: 'include' // <-- THIS IS ALSO GOOD PRACTICE
            });

            if (response.ok) {
                setLoggedInUserName(null);
                localStorage.removeItem('userName');
                showNotification(t('logged_out_success'), 'success');
            } else {
                showNotification(t('logout_failed'), 'error');
                console.error('Logout failed:', response.status, response.statusText);
            }
        } catch (error) {
            console.error('Network error during logout:', error);
            showNotification(t('network_error_logout'), 'error');
        }
    };

    const handleLanguageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        i18n.changeLanguage(event.target.value);
    };

    const headerBgClass = isHomePage && !isScrolled ? 'bg-transparent' : 'bg-white shadow-lg';
    const textColorClass = isHomePage && !isScrolled ? 'text-white' : 'text-gray-800';
    const mobileMenuBgClass = isHomePage && !isScrolled ? 'bg-[#1A237E]' : 'bg-white';

    const mobileMenuVariants: Variants = {
        hidden: { opacity: 0, y: -50 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" } },
        exit: { opacity: 0, y: -50, transition: { duration: 0.2, ease: "easeIn" } }
    };

    return (
        <header
            className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${headerBgClass}`}
        >
            <AnimatePresence>
                {notification && (
                    <motion.div
                        initial={{ opacity: 0, y: -50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -50 }}
                        className={`fixed top-4 left-1/2 -translate-x-1/2 p-4 rounded-lg shadow-lg z-[1000] text-white font-medium
                            ${notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}
                    >
                        {notification.message}
                    </motion.div>
                )}
            </AnimatePresence>

            <nav className="container mx-auto px-6 py-4 flex items-center justify-between">
                <div className="flex items-center">
                    {/* Use Link for internal navigation */}
                    <Link to="/" className={`text-2xl font-bold ${textColorClass}`}>
                        <img src={logo} alt="COC Logo" className="h-14 md:h-14" />
                    </Link>
                </div>

                <div className="hidden md:flex items-center space-x-8">
                    {/* Use Link for internal navigation */}
                    <Link to="/android" className={`hover:text-[#AEEA00] transition-colors duration-200 font-medium ${textColorClass}`}>{t('android_app')}</Link>
                    <Link to="/iphone" className={`hover:text-[#AEEA00] transition-colors duration-200 font-medium ${textColorClass}`}>{t('iphone_app')} <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full ml-1 animate-pulse">{t('new_tag')}</span></Link>
                    <Link to="/community" className={`hover:text-[#AEEA00] transition-colors duration-200 font-medium ${textColorClass}`}>{t('community')}</Link>
                    <Link to="/solutions" className={`hover:text-[#AEEA00] transition-colors duration-200 font-medium ${textColorClass}`}>{t('solutions')}</Link>
                    <Link to="/coc-alert" className={`hover:text-[#AEEA00] transition-colors duration-200 font-medium ${textColorClass}`}>{t('coc_alert')} <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full ml-1 animate-pulse">{t('new_tag')}</span></Link>
                </div>

                <div className="hidden md:flex items-center space-x-4">
                    <div className="relative">
                        <select
                            className={`appearance-none bg-transparent py-2 px-3 pr-8 rounded-md cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#AEEA00] ${textColorClass}`}
                            value={i18n.language}
                            onChange={handleLanguageChange}
                        >
                            <option value="en" className="text-gray-800">{t('language_en')}</option>
                            <option value="hi" className="text-gray-800">{t('language_hi')}</option>
                        </select>
                        <div className={`pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 ${textColorClass}`}>
                            <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                        </div>
                    </div>

                    {loggedInUserName ? (
                        <div className={`relative group py-2 px-4 rounded-full transition-colors duration-200 ${textColorClass} border border-current flex items-center gap-2 cursor-pointer`}>
                            <FaUserCircle className="text-xl" />
                            <span className="font-medium">{loggedInUserName}</span>
                            <div className="absolute top-full left-0 mt-2 w-full min-w-[120px] bg-white text-gray-800 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform scale-y-0 origin-top group-hover:scale-y-100">
                                <button
                                    onClick={handleLogout}
                                    className="block w-full text-left px-4 py-2 hover:bg-gray-100 rounded-md"
                                >
                                    {t('logout_button')}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button
                            onClick={() => setIsAuthModalOpen(true)}
                            className={`py-2 px-4 rounded-full transition-colors duration-200 ${textColorClass} border border-current hover:bg-gray-100 hover:text-[#1A237E]`}
                        >
                            {t('sign_in_button')}
                        </button>
                    )}

                    <motion.a
                        href="/download"
                        className={`py-2 px-6 rounded-full font-bold shadow-md transition-colors duration-300
                            ${isHomePage && !isScrolled ? 'bg-white text-[#1A237E] hover:bg-gray-200' : 'bg-[#AEEA00] text-[#1A237E] hover:bg-lime-600'}`}
                        whileHover={{ scale: 1.05, boxShadow: "0 4px 15px rgba(0,0,0,0.2)" }}
                        whileTap={{ scale: 0.95 }}
                    >
                        {t('download_app_button')}
                    </motion.a>
                </div>

                <div className="md:hidden flex items-center space-x-4">
                    <div className="relative">
                        <select
                            className={`appearance-none bg-transparent py-2 px-3 pr-8 rounded-md cursor-pointer focus:outline-none ${textColorClass} text-sm`}
                            value={i18n.language}
                            onChange={handleLanguageChange}
                        >
                            <option value="en" className="text-gray-800">{t('language_en')}</option>
                            <option value="hi" className="text-gray-800">{t('language_hi')}</option>
                        </select>
                        <div className={`pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 ${textColorClass}`}>
                            <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                        </div>
                    </div>

                    <button
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        className={`p-2 rounded-md focus:outline-none ${textColorClass}`}
                    >
                        {isMobileMenuOpen ? <FaTimes className="text-2xl" /> : <FaBars className="text-2xl" />}
                    </button>
                </div>
            </nav>

            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        variants={mobileMenuVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className={`md:hidden ${mobileMenuBgClass} py-4 border-t ${isHomePage && !isScrolled ? 'border-blue-700' : 'border-gray-200'}`}
                    >
                        <div className="flex flex-col items-center space-y-4">
                            {/* Use Link for internal navigation */}
                            <Link to="/android" className={`block py-2 ${textColorClass} hover:text-[#AEEA00] transition-colors`}>{t('android_app')}</Link>
                            <Link to="/iphone" className={`block py-2 ${textColorClass} hover:text-[#AEEA00] transition-colors`}>{t('iphone_app')}</Link>
                            <Link to="/community" className={`block py-2 ${textColorClass} hover:text-[#AEEA00] transition-colors`}>{t('community')}</Link>
                            <Link to="/solutions" className={`block py-2 ${textColorClass} hover:text-[#AEEA00] transition-colors`}>{t('solutions')}</Link>
                            <Link to="/coc-alert" className={`block py-2 ${textColorClass} hover:text-[#AEEA00] transition-colors`}>{t('coc_alert')} <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full ml-1">{t('new_tag')}</span></Link>
                            {loggedInUserName ? (
                                <>
                                <div className={`block py-2 ${textColorClass} flex items-center gap-2`}>
                                    <FaUserCircle className="text-xl" />
                                    <span className="font-medium">{loggedInUserName}</span>
                                </div>
                                <button
                                    onClick={handleLogout}
                                    className={`block py-2 ${textColorClass} hover:text-[#AEEA00] transition-colors`}
                                >
                                    {t('logout_button')}
                                </button>
                                </>
                            ) : (
                                <button
                                    onClick={() => { setIsAuthModalOpen(true); setIsMobileMenuOpen(false); }}
                                    className={`block py-2 ${textColorClass} hover:text-[#AEEA00] transition-colors`}
                                >
                                    {t('sign_in_button')}
                                </button>
                            )}
                            <motion.a
                                href="/download"
                                className={`py-2 px-6 rounded-full font-bold shadow-md transition-colors duration-300
                                    ${isHomePage && !isScrolled ? 'bg-white text-[#1A237E] hover:bg-gray-200' : 'bg-[#AEEA00] text-[#1A237E] hover:bg-lime-600'}`}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                {t('download_app_button')}
                            </motion.a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {isAuthModalOpen && (
                    <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} onLoginSuccess={handleLoginSuccess} />
                )}
            </AnimatePresence>
        </header>
    );
};

export default Header;